import { useEffect, useState } from 'react';
import { TextField, SelectField, type CreateTechnologyFormData } from '../forms';
import { createTechnologySchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { listSuppliers, type Supplier } from '../../lib/apiClient';
import { useTechnologyProductFamilies } from '../../graphql/queries';
import { CreateSupplierDialog } from './CreateSupplierDialog';
import '@tylertech/forge/button';
import '@tylertech/forge/card';
import './TechnologyForm.css';

export interface TechnologyFormProps {
  defaultValues?: Partial<CreateTechnologyFormData>;
  onSubmit: (data: CreateTechnologyFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
}

const lifecycleStageOptions = [
  { value: 'Adopt', label: 'Adopt' },
  { value: 'Hold', label: 'Hold' },
  { value: 'Evaluate', label: 'Evaluate' },
  { value: 'Sunset', label: 'Sunset' },
];

const securityRatingOptions = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const licenseTypeOptions = [
  { value: 'open-source', label: 'Open Source' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'custom', label: 'Custom' },
];

const CardElement = 'forge-card' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export function TechnologyForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: TechnologyFormProps): JSX.Element {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const { data: familiesData } = useTechnologyProductFamilies();

  useEffect(() => {
    listSuppliers()
      .then(setSuppliers)
      .catch(() => {});
  }, []);

  // value must be the supplier's entityId, not its display name (issue #1458)
  // — the backend filter/FK check does an exact match against
  // TechnologySupplier.entityId, same shape as the Product Family fix below
  // (issue #1447).
  const supplierOptions = suppliers.map((s) => ({ value: s.entityId, label: s.name }));
  // value must be the family's entityId, not its display name (issue #1447)
  // — the backend filter/FK check does an exact match against
  // TechnologyProductFamily.entityId, mirroring the list-page filter dropdown
  // (routes/technologies/index.tsx).
  const familyOptions = [...(familiesData?.technologyProductFamilies ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((f) => ({ value: f.entityId, label: f.name }));

  return (
    <EntityForm<CreateTechnologyFormData>
      schema={createTechnologySchema}
      baseValues={{
        lifecycleStage: 'Adopt',
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Technology"
      editLabel="Update Technology"
      formClassName="technology-form"
    >
      {({ register, formState: { errors }, values: formValues, setValue }) => {
        const handleSupplierCreated = (supplier: Supplier): void => {
          setSuppliers((prev) => [...prev, supplier]);
          // entityId, not name — same FK-drift shape as the family fix above
          // (issue #1458).
          setValue('supplierId', supplier.entityId);
          setShowCreateSupplier(false);
        };

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Technology Name"
                  required
                  error={errors.name}
                  {...register('name')}
                />

                <TextField
                  label="Description"
                  error={errors.description}
                  {...register('description')}
                />

                <SelectField
                  label="Product Family"
                  options={familyOptions}
                  error={errors.productFamilyId}
                  value={formValues.productFamilyId ?? ''}
                  {...register('productFamilyId')}
                />

                <TextField label="Version" error={errors.version} {...register('version')} />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Lifecycle Stage</h2>

                <SelectField
                  label="Lifecycle Stage"
                  options={lifecycleStageOptions}
                  error={errors.lifecycleStage}
                  value={formValues.lifecycleStage ?? ''}
                  {...register('lifecycleStage')}
                />

                <SelectField
                  label="Security Rating"
                  options={securityRatingOptions}
                  error={errors.securityRating}
                  value={(formValues.securityRating as string | undefined) ?? ''}
                  {...register('securityRating')}
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Vendor &amp; Licensing</h2>

                <div className="technology-form__supplier-row">
                  <div className="technology-form__supplier-field">
                    <SelectField
                      label="Supplier"
                      options={supplierOptions}
                      error={errors.supplierId}
                      value={formValues.supplierId ?? ''}
                      {...register('supplierId')}
                    />
                  </div>
                  <ForgeButton
                    type="button"
                    variant="outlined"
                    onClick={() => setShowCreateSupplier(true)}
                    className="technology-form__supplier-add-button"
                    style={{ marginBottom: errors.supplierId ? '20px' : '0' }}
                  >
                    + Supplier
                  </ForgeButton>
                </div>

                <SelectField
                  label="License Type"
                  options={licenseTypeOptions}
                  error={errors.licenseType}
                  value={(formValues.licenseType as string | undefined) ?? ''}
                  {...register('licenseType')}
                />

                <TextField
                  label="Support End Date"
                  type="date"
                  error={errors.supportEndDate}
                  {...register('supportEndDate')}
                />

                <TextField label="Notes" error={errors.notes} {...register('notes')} />
              </div>
            </CardElement>

            <CreateSupplierDialog
              open={showCreateSupplier}
              onClose={() => setShowCreateSupplier(false)}
              onCreated={handleSupplierCreated}
            />
          </>
        );
      }}
    </EntityForm>
  );
}
